import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Empty, Input, List, Modal, Radio, Select, Space, Spin, Switch, Tag, Tooltip, Typography, message } from 'antd'
import { ArrowDownOutlined, ArrowUpOutlined, CopyOutlined, DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons'
import {
    leadsApi,
    type FirstMessageRotation,
    type FirstMessageRule,
    type FirstMessageRulePayload,
    type FirstMessageRuleVariant,
    type SourceTemplate,
} from '../services/leadsApi'
import TgRichEditor from './TgRichEditor'
import TgMessagePreview from './TgMessagePreview'
import FormattedText from './FormattedText'
import { templateToTg, tgToPlain } from '../utils/tgFormat'
import { TEMPERATURE_OPTIONS } from '../constants/leadTemperature'

// Правила первого сообщения: текст зависит от статуса, гео, источника и
// температуры лида. Лид получает текст первого правила, все условия которого
// совпали; правило без условий - общее, «на всё остальное», и проверяется
// последним.

const { Text, Title } = Typography

type ConditionKey = 'statuses' | 'geos' | 'sources' | 'temperatures'

const CONDITIONS: Array<{ key: ConditionKey; label: string; hint: string }> = [
    { key: 'statuses', label: 'Статус', hint: 'Статус лида в CRM Wiseway - тот, что виден в шапке чата' },
    { key: 'geos', label: 'ГЕО', hint: 'Гео лида' },
    { key: 'sources', label: 'Источник', hint: 'Источник лида из CRM Wiseway' },
    { key: 'temperatures', label: 'Температура', hint: 'Температура лида' },
]

const CONDITION_LABEL: Record<ConditionKey, string> = {
    statuses: 'Статус',
    geos: 'ГЕО',
    sources: 'Источник',
    temperatures: 'Температура',
}

interface RuleDraft {
    name: string
    enabled: boolean
    statuses: string[]
    geos: string[]
    sources: string[]
    temperatures: string[]
    // Тексты правила в формате Telegram. Их может быть несколько.
    texts: string[]
    rotation: FirstMessageRotation
    // Какие условия сейчас показаны: условие может быть добавлено, но ещё пустое.
    shown: ConditionKey[]
}

function extractError(error: unknown): string {
    if (typeof error === 'object' && error && 'response' in error) {
        const response = (error as { response?: { data?: { detail?: string } } }).response
        return response?.data?.detail || 'Ошибка запроса'
    }
    return 'Ошибка запроса'
}

function toDraft(rule: FirstMessageRule): RuleDraft {
    const shown = CONDITIONS.map((item) => item.key).filter((key) => (rule[key] || []).length > 0)
    return {
        name: rule.name,
        enabled: rule.enabled,
        statuses: rule.statuses || [],
        geos: rule.geos || [],
        sources: rule.sources || [],
        temperatures: rule.temperatures || [],
        texts: (rule.variants || []).length
            ? (rule.variants || []).map((variant) => templateToTg(variant.content, variant.is_formatted))
            : [''],
        rotation: rule.rotation || 'round_robin',
        shown,
    }
}

function conditionSummary(rule: FirstMessageRule, statusLabels: Record<string, string>): Array<{ key: ConditionKey; text: string }> {
    return CONDITIONS
        .filter((item) => (rule[item.key] || []).length > 0)
        .map((item) => {
            // Статусы хранятся кодами CRM - показываем их названия.
            const values = (rule[item.key] || []).map((value) => (item.key === 'statuses' ? statusLabels[value] || value : value))
            return { key: item.key, text: `${item.label}: ${values.join(', ')}` }
        })
}

export default function FirstMessageRules({ sources }: { sources: SourceTemplate[] }) {
    const [rules, setRules] = useState<FirstMessageRule[]>([])
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [selectedId, setSelectedId] = useState<number | null>(null)
    const [draft, setDraft] = useState<RuleDraft | null>(null)

    // Статусы CRM Wiseway по группам: этапы воронки и архив.
    const [statusGroups, setStatusGroups] = useState<Array<{ group: string; statuses: Array<{ code: string; label: string }> }>>([])
    const statusLabels = useMemo(() => {
        const labels: Record<string, string> = {}
        statusGroups.forEach((group) => group.statuses.forEach((item) => { labels[item.code] = item.label }))
        return labels
    }, [statusGroups])
    const statusOptions = useMemo(() => statusGroups.map((group) => ({
        label: group.group,
        options: group.statuses.map((item) => ({ value: item.code, label: item.label })),
    })), [statusGroups])
    const [geos, setGeos] = useState<string[]>([])

    // Проверка «какое правило сработает».
    const [checkOpen, setCheckOpen] = useState(false)
    const [check, setCheck] = useState<{ status?: string; geo?: string; source?: string; temperature?: string }>({})
    const [checkResult, setCheckResult] = useState<{
        rule_name: string | null
        content: string | null
        is_formatted: boolean
        variants: FirstMessageRuleVariant[]
        rotation: FirstMessageRotation | null
    } | null>(null)

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const response = await leadsApi.getFirstMessageRules()
            setRules(response.data)
            setSelectedId((current) => (current && response.data.some((rule) => rule.id === current)
                ? current
                : response.data[0]?.id ?? null))
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
        leadsApi.getFirstMessageRuleStatuses().then((response) => setStatusGroups(response.data.groups)).catch(() => undefined)
        leadsApi.getGeos().then((response) => setGeos(response.data.map((geo) => geo.name))).catch(() => undefined)
    }, [load])

    const selected = useMemo(() => rules.find((rule) => rule.id === selectedId) || null, [rules, selectedId])

    // Черновик подставляется при выборе правила, чтобы правки одного правила
    // не утекали в другое.
    useEffect(() => {
        setDraft(selected ? toDraft(selected) : null)
    }, [selected])

    // Плоский список значений или сгруппированный (статусы CRM - по группам).
    type ConditionOption = { value: string; label: string } | { label: string; options: Array<{ value: string; label: string }> }
    const valueOptions = (key: ConditionKey): ConditionOption[] => {
        if (key === 'statuses') return statusOptions
        if (key === 'geos') return geos.map((geo) => ({ value: geo, label: geo }))
        if (key === 'sources') return sources.map((item) => ({ value: item.source, label: item.source }))
        return TEMPERATURE_OPTIONS.map((item) => ({ value: item.value, label: item.label }))
    }

    const payload = (value: RuleDraft): FirstMessageRulePayload => ({
        name: value.name.trim() || 'Без названия',
        enabled: value.enabled,
        // Добавленное, но незаполненное условие ничего не ограничивает.
        statuses: value.shown.includes('statuses') ? value.statuses : [],
        geos: value.shown.includes('geos') ? value.geos : [],
        sources: value.shown.includes('sources') ? value.sources : [],
        temperatures: value.shown.includes('temperatures') ? value.temperatures : [],
        // Пустые тексты не сохраняем: правило с пустым текстом не отправляет.
        variants: value.texts
            .filter((text) => tgToPlain(text).trim())
            .map((text): FirstMessageRuleVariant => ({ content: text, is_formatted: true })),
        rotation: value.rotation,
        content: value.texts[0] || '',
        is_formatted: true,
    })

    const saveRule = async () => {
        if (!selected || !draft) return
        setSaving(true)
        try {
            await leadsApi.updateFirstMessageRule(selected.id, payload(draft))
            await load()
            message.success('Правило сохранено')
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setSaving(false)
        }
    }

    const createRule = async (source?: FirstMessageRule) => {
        try {
            const response = await leadsApi.createFirstMessageRule(source
                ? { ...payload(toDraft(source)), name: `${source.name} (копия)` }
                : {
                    name: 'Новое правило', enabled: true,
                    statuses: [], geos: [], sources: [], temperatures: [],
                    variants: [], rotation: 'round_robin',
                    content: '', is_formatted: true,
                })
            await load()
            setSelectedId(response.data.id)
        } catch (error) {
            message.error(extractError(error))
        }
    }

    const removeRule = (rule: FirstMessageRule) => {
        Modal.confirm({
            title: `Удалить правило «${rule.name}»?`,
            content: 'Лиды, которые попадали под это правило, будут проверяться остальными правилами.',
            okText: 'Удалить',
            cancelText: 'Отмена',
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await leadsApi.deleteFirstMessageRule(rule.id)
                    await load()
                } catch (error) {
                    message.error(extractError(error))
                }
            },
        })
    }

    const move = async (rule: FirstMessageRule, shift: -1 | 1) => {
        const ids = rules.map((item) => item.id)
        const index = ids.indexOf(rule.id)
        const next = index + shift
        if (index < 0 || next < 0 || next >= ids.length) return
        ids.splice(next, 0, ids.splice(index, 1)[0])
        setRules((current) => ids.map((id) => current.find((item) => item.id === id)!).filter(Boolean))
        try {
            const response = await leadsApi.reorderFirstMessageRules(ids)
            setRules(response.data)
        } catch (error) {
            message.error(extractError(error))
            await load()
        }
    }

    const toggleEnabled = async (rule: FirstMessageRule, enabled: boolean) => {
        try {
            await leadsApi.updateFirstMessageRule(rule.id, { ...payload(toDraft(rule)), enabled })
            await load()
        } catch (error) {
            message.error(extractError(error))
        }
    }

    const runCheck = async () => {
        try {
            const response = await leadsApi.checkFirstMessageRule(check)
            setCheckResult(response.data)
        } catch (error) {
            message.error(extractError(error))
        }
    }

    // Правило, которое никогда не сработает: выше него стоит общее правило.
    const firstFallbackIndex = rules.findIndex((rule) => rule.is_fallback && rule.enabled)
    // Общее правило «на всё остальное»: его текст получает лид, не подошедший
    // ни под одно правило с условиями.
    const fallbackRule = firstFallbackIndex >= 0 ? rules[firstFallbackIndex] : null

    const addCondition = (key: ConditionKey) => {
        setDraft((current) => (current && !current.shown.includes(key)
            ? { ...current, shown: [...current.shown, key] }
            : current))
    }

    const removeCondition = (key: ConditionKey) => {
        setDraft((current) => (current
            ? { ...current, shown: current.shown.filter((item) => item !== key), [key]: [] }
            : current))
    }

    return (
        <div>
            <Space style={{ width: '100%', justifyContent: 'space-between' }} align="center">
                <Title level={5} style={{ margin: 0 }}>Правила первого сообщения</Title>
                <Space>
                    <Button onClick={() => { setCheckResult(null); setCheckOpen(true) }}>Проверить правило</Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => createRule()}>
                        Новое правило
                    </Button>
                </Space>
            </Space>

            <Alert
                type="info"
                showIcon
                style={{ margin: '14px 0 10px' }}
                message="Лид получает текст первого правила сверху, все условия которого совпали. Условия добавляются кнопкой «+»: несколько значений внутри условия — «или», разные условия — «и». Правило без условий общее, «на всё остальное», и проверяется последним."
            />

            {/* Общее правило важно видеть сразу: без него лиды, не подошедшие
                ни под одно правило, остаются в очереди. */}
            {fallbackRule ? (
                <Alert
                    type="success"
                    showIcon
                    style={{ marginBottom: 14 }}
                    message={
                        <Space wrap>
                            <span>Общее правило «на всё остальное»: <b>{fallbackRule.name}</b></span>
                            {!fallbackRule.is_filled && <Tag color="red">текст пустой</Tag>}
                            <Button size="small" onClick={() => setSelectedId(fallbackRule.id)}>Открыть</Button>
                        </Space>
                    }
                />
            ) : (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 14 }}
                    message="Общее правило не задано"
                    description="Лид, не подошедший ни под одно правило, останется в очереди без сообщения. Создайте правило и не добавляйте ему условий — оно станет общим."
                    action={<Button size="small" onClick={() => createRule()}>Создать</Button>}
                />
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 360px) minmax(0, 1fr)', gap: 16 }}>
                <Spin spinning={loading}>
                    <div style={{ maxHeight: 460, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
                        <List
                            dataSource={rules}
                            locale={{ emptyText: <Empty description="Правил пока нет" /> }}
                            renderItem={(rule, index) => (
                                <List.Item
                                    onClick={() => setSelectedId(rule.id)}
                                    style={{
                                        cursor: 'pointer',
                                        padding: '10px 12px',
                                        background: selectedId === rule.id ? 'var(--lead-selected-bg)' : undefined,
                                    }}
                                >
                                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                        <Space style={{ width: '100%', justifyContent: 'space-between' }} align="start">
                                            <Space size={6} wrap>
                                                <Text strong={selectedId === rule.id}>{index + 1}. {rule.name}</Text>
                                                {rule.is_fallback && <Tag color="purple">на всё остальное</Tag>}
                                                {!rule.is_filled && <Tag color="red">текст пустой</Tag>}
                                                {(rule.variants || []).length > 1 && (
                                                    <Tag color="blue">
                                                        {rule.variants.length} текста · {rule.rotation === 'random' ? 'случайно' : 'по кругу'}
                                                    </Tag>
                                                )}
                                            </Space>
                                            <Space size={2} onClick={(event) => event.stopPropagation()}>
                                                <Tooltip title="Выше">
                                                    <Button size="small" type="text" icon={<ArrowUpOutlined />} disabled={index === 0} onClick={() => move(rule, -1)} />
                                                </Tooltip>
                                                <Tooltip title="Ниже">
                                                    <Button size="small" type="text" icon={<ArrowDownOutlined />} disabled={index === rules.length - 1} onClick={() => move(rule, 1)} />
                                                </Tooltip>
                                                <Tooltip title="Дублировать">
                                                    <Button size="small" type="text" icon={<CopyOutlined />} onClick={() => createRule(rule)} />
                                                </Tooltip>
                                                <Tooltip title="Удалить">
                                                    <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => removeRule(rule)} />
                                                </Tooltip>
                                                <Tooltip title={rule.enabled ? 'Включено' : 'Выключено'}>
                                                    <Switch size="small" checked={rule.enabled} onChange={(value) => toggleEnabled(rule, value)} />
                                                </Tooltip>
                                            </Space>
                                        </Space>
                                        <Space size={4} wrap>
                                            {conditionSummary(rule, statusLabels).map((item) => (
                                                <Tag key={item.key} style={{ marginInlineEnd: 0 }}>{item.text}</Tag>
                                            ))}
                                            {rule.is_fallback && <Text type="secondary" style={{ fontSize: 12 }}>условий нет</Text>}
                                        </Space>
                                        {firstFallbackIndex >= 0 && index > firstFallbackIndex && !rule.is_fallback && (
                                            <Text type="warning" style={{ fontSize: 12 }}>
                                                Общее правило стоит выше — это правило тоже сработает, но только если общее выключить
                                            </Text>
                                        )}
                                    </Space>
                                </List.Item>
                            )}
                        />
                    </div>
                </Spin>

                <div>
                    {draft && selected ? (
                        <Space direction="vertical" size={10} style={{ width: '100%' }}>
                            <Space wrap>
                                <Text strong>Название</Text>
                                <Input
                                    value={draft.name}
                                    onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                                    style={{ width: 320 }}
                                    maxLength={255}
                                />
                                <Switch
                                    checked={draft.enabled}
                                    onChange={(value) => setDraft({ ...draft, enabled: value })}
                                    checkedChildren="вкл"
                                    unCheckedChildren="выкл"
                                />
                            </Space>

                            <Space direction="vertical" size={6} style={{ width: '100%' }}>
                                <Space wrap>
                                    <Text strong>Условия</Text>
                                    {CONDITIONS.filter((item) => !draft.shown.includes(item.key)).map((item) => (
                                        <Tooltip key={item.key} title={item.hint}>
                                            <Button size="small" icon={<PlusOutlined />} onClick={() => addCondition(item.key)}>
                                                {item.label}
                                            </Button>
                                        </Tooltip>
                                    ))}
                                    {draft.shown.length === 0 && (
                                        <Text type="secondary">условий нет — правило станет общим, «на всё остальное»</Text>
                                    )}
                                </Space>
                                {draft.shown.map((key) => (
                                    <Space key={key} style={{ width: '100%' }} align="center">
                                        <Text style={{ width: 110, display: 'inline-block' }}>{CONDITION_LABEL[key]}</Text>
                                        <Select
                                            mode="multiple"
                                            allowClear
                                            style={{ minWidth: 420 }}
                                            placeholder="Выберите одно или несколько значений"
                                            value={draft[key]}
                                            onChange={(value: string[]) => setDraft({ ...draft, [key]: value })}
                                            options={valueOptions(key)}
                                            optionFilterProp="label"
                                            // Статусов CRM сорок - список выше, чтобы не прятать их за прокруткой.
                                            listHeight={420}
                                            popupClassName="rule-condition-dropdown"
                                        />
                                        <Button size="small" type="text" danger onClick={() => removeCondition(key)}>×</Button>
                                    </Space>
                                ))}
                            </Space>

                            <Space wrap>
                                <Text strong>Тексты правила</Text>
                                <Button
                                    size="small"
                                    icon={<PlusOutlined />}
                                    onClick={() => setDraft({ ...draft, texts: [...draft.texts, ''] })}
                                >
                                    Добавить текст
                                </Button>
                                {draft.texts.length > 1 && (
                                    <>
                                        <Text type="secondary">Как отправлять:</Text>
                                        <Radio.Group
                                            size="small"
                                            optionType="button"
                                            buttonStyle="solid"
                                            value={draft.rotation}
                                            onChange={(event) => setDraft({ ...draft, rotation: event.target.value })}
                                            options={[
                                                { value: 'round_robin', label: 'По кругу' },
                                                { value: 'random', label: 'Случайно' },
                                            ]}
                                        />
                                    </>
                                )}
                            </Space>
                            {draft.texts.length > 1 && (
                                <Text type="secondary">
                                    {draft.rotation === 'random'
                                        ? 'Каждому лиду достаётся случайный текст из списка.'
                                        : 'Тексты уходят по очереди: первому лиду — первый текст, следующему — второй и так по кругу.'}
                                </Text>
                            )}
                            {draft.texts.map((text, index) => (
                                <div key={index} style={{ borderTop: index ? '1px solid var(--border)' : undefined, paddingTop: index ? 12 : 0 }}>
                                    <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 6 }}>
                                        <Text type="secondary">Текст {index + 1} из {draft.texts.length}</Text>
                                        {draft.texts.length > 1 && (
                                            <Button
                                                size="small"
                                                type="text"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() => setDraft({ ...draft, texts: draft.texts.filter((_, item) => item !== index) })}
                                            >
                                                Удалить текст
                                            </Button>
                                        )}
                                    </Space>
                                    <TgRichEditor
                                        value={text}
                                        onChange={(value) => setDraft({
                                            ...draft,
                                            texts: draft.texts.map((item, position) => (position === index ? value : item)),
                                        })}
                                        minRows={7}
                                        maxRows={16}
                                        placeholder="Если оставить пустым, этот текст не будет отправляться"
                                    />
                                    <TgMessagePreview value={text} />
                                </div>
                            ))}
                            <Text type="secondary">Доступные переменные: {'{name}'}, {'{title}'}, {'{geo}'}, {'{source}'}, {'{telegram}'}</Text>
                            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={saveRule}>
                                Сохранить правило
                            </Button>
                        </Space>
                    ) : (
                        <Empty description="Выберите правило слева или создайте новое" />
                    )}
                </div>
            </div>

            <Modal
                open={checkOpen}
                title="Какое правило сработает"
                onCancel={() => setCheckOpen(false)}
                onOk={runCheck}
                okText="Проверить"
                cancelText="Закрыть"
                width={620}
            >
                <Space direction="vertical" size={10} style={{ width: '100%' }}>
                    <Text type="secondary">Укажите данные лида — покажу, какое правило сработает и какой текст он получит.</Text>
                    <Select
                        allowClear style={{ width: '100%' }} placeholder="Статус"
                        value={check.status} onChange={(value) => setCheck({ ...check, status: value })}
                        options={statusOptions} optionFilterProp="label" showSearch listHeight={420} popupClassName="rule-condition-dropdown"
                    />
                    <Select
                        allowClear style={{ width: '100%' }} placeholder="ГЕО"
                        value={check.geo} onChange={(value) => setCheck({ ...check, geo: value })}
                        options={geos.map((geo) => ({ value: geo, label: geo }))} optionFilterProp="label" showSearch
                    />
                    <Select
                        allowClear style={{ width: '100%' }} placeholder="Источник"
                        value={check.source} onChange={(value) => setCheck({ ...check, source: value })}
                        options={sources.map((item) => ({ value: item.source, label: item.source }))} optionFilterProp="label" showSearch
                    />
                    <Select
                        allowClear style={{ width: '100%' }} placeholder="Температура"
                        value={check.temperature} onChange={(value) => setCheck({ ...check, temperature: value })}
                        options={TEMPERATURE_OPTIONS.map((item) => ({ value: item.value, label: item.label }))}
                    />
                    {checkResult && (
                        checkResult.rule_name ? (
                            <Alert
                                type="success"
                                showIcon
                                message={`Сработает правило «${checkResult.rule_name}»`}
                                description={(checkResult.variants || []).length ? (
                                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                        {checkResult.variants.length > 1 && (
                                            <Text type="secondary">
                                                Текстов несколько, отправляются {checkResult.rotation === 'random' ? 'случайно' : 'по кругу'}:
                                            </Text>
                                        )}
                                        {checkResult.variants.map((variant, index) => (
                                            <div key={index}>
                                                {checkResult.variants.length > 1 && <Text type="secondary">Текст {index + 1}:</Text>}
                                                <div>
                                                    {variant.is_formatted
                                                        ? <FormattedText html={variant.content} />
                                                        : <span style={{ whiteSpace: 'pre-wrap' }}>{variant.content}</span>}
                                                </div>
                                            </div>
                                        ))}
                                    </Space>
                                ) : 'У правила нет ни одного текста — рассылка не пойдёт'}
                            />
                        ) : (
                            <Alert
                                type="warning"
                                showIcon
                                message="Ни одно правило не подходит"
                                description="Лид останется в очереди. Добавьте общее правило «на всё остальное» — правило без условий."
                            />
                        )
                    )}
                    {checkResult?.content && !tgToPlain(checkResult.content).trim() && (
                        <Text type="secondary">Текст правила пустой.</Text>
                    )}
                </Space>
            </Modal>
        </div>
    )
}
